import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError, BACKEND } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import FileUploader from "../../components/admin/FileUploader";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, PlayCircle } from "@phosphor-icons/react";
import { SortableList, SortableItem, DragHandle } from "../../components/admin/SortableList";

const empty = {
  title: "",
  description: "",
  youtube_url: "",
  thumbnail_url: "",
  is_active: true,
};

// Extract YouTube ID from any share/watch URL so we can generate a thumbnail preview.
const parseYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([\w-]{6,})/,
    /youtube\.com\/shorts\/([\w-]{6,})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
};

export default function Videos() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/admin/videos");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    if (!form.youtube_url) { toast.error("YouTube URL is required"); return; }
    try {
      if (editing) await api.put(`/admin/videos/${editing.id}`, form);
      else await api.post("/admin/videos", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete video?")) return;
    try { await api.delete(`/admin/videos/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Videos"
        subtitle="YouTube video library. Shown on the public /videos page and the homepage Capability strip."
        actions={
          <Button onClick={openNew} data-testid="add-video-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />New video
          </Button>
        }
      />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="videos-grid">
          {items.length === 0 && <div className="col-span-full text-center py-12 text-[#4B5563]">No videos yet. Click <b>New video</b> to add one.</div>}
          <SortableList items={items} setItems={setItems} reorderEndpoint="/admin/reorder/videos" layout="grid">
            {items.map((it) => {
              const yt = parseYouTubeId(it.youtube_url);
              const thumb = it.thumbnail_url
                ? (it.thumbnail_url.startsWith("http") ? it.thumbnail_url : `${BACKEND}${it.thumbnail_url}`)
                : (yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : null);
              return (
                <SortableItem key={it.id} id={it.id} as="div" className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`video-card-${it.id}`}>
                  {(handleProps) => (
                    <>
                      <div className="relative aspect-video bg-[#0F172A] overflow-hidden">
                        <div className="absolute top-2 left-2 z-10 bg-white/90 rounded p-1 shadow"><DragHandle handleProps={handleProps} /></div>
                        {thumb ? (
                          <img src={thumb} alt={it.title} className="w-full h-full object-cover opacity-80" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#C2410C]/50 to-[#0F172A]" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <PlayCircle size={54} weight="fill" className="text-white/90 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]" />
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="font-medium text-[#1F2937] truncate">{it.title}</div>
                        <div className="text-xs text-[#4B5563] truncate">{it.youtube_url}</div>
                        <div className="mt-3 flex items-center justify-between">
                          <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${it.is_active ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"}`}>
                            {it.is_active ? "Live" : "Hidden"}
                          </span>
                          <div>
                            <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                            <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </SortableItem>
              );
            })}
          </SortableList>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="video-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit video" : "New video"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="video-title" />
            </div>
            <div className="space-y-2">
              <Label>YouTube URL *</Label>
              <Input
                value={form.youtube_url}
                onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                placeholder="https://youtu.be/dQw4w9WgXcQ"
                data-testid="video-youtube-url"
              />
              <p className="text-xs text-[#4B5563]">Any YouTube share link. Thumbnail is auto-fetched unless overridden below.</p>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="video-description" />
            </div>
            <FileUploader
              kind="image"
              accept="image/*"
              label="Custom thumbnail (optional)"
              currentUrl={form.thumbnail_url}
              onUploaded={(url) => setForm({ ...form, thumbnail_url: url })}
              testId="video-thumbnail"
            />
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="video-active" />
              <Label>Live on public site</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-video" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
